import React, { useEffect, useMemo } from 'react';
import { Box, Switch, Typography } from '@mui/material';
import {
  FormBuilder,
  FormSelect,
  scrollToFirstFormError,
} from '../../../components/ui';
import {
  getProfileFormFieldOrder,
  getProfileFormSections,
  SESSION_DAY_OPTIONS,
  SESSION_HOUR_OPTIONS,
  SESSION_MINUTE_OPTIONS,
} from '../config/profileFormConfig';

export default function ProfileForm({
  form,
  errors,
  updateField,
  validationAttempt = 0,
  showIsStandard = false,
  organisationName = '',
  isEditMode = false,
}) {
  const sections = useMemo(
    () => getProfileFormSections({ showIsStandard, organisationName, isEditMode }),
    [isEditMode, organisationName, showIsStandard]
  );
  const fieldOrder = useMemo(() => getProfileFormFieldOrder(sections), [sections]);

  useEffect(() => {
    if (validationAttempt > 0) {
      scrollToFirstFormError(errors, fieldOrder);
    }
  }, [errors, fieldOrder, validationAttempt]);

  const renderProfileField = (field, ctx) => {
    const { name, label, hint, required } = field;
    const errorMessage = ctx.errors[name];
    const commonSelectProps = {
      label,
      hint,
      required,
      value: ctx.values[name] ?? 0,
      error: Boolean(errorMessage),
      helperText: errorMessage,
      disabled: ctx.disabled,
      onChange: (event) => ctx.onFieldChange(name, Number(event.target.value)),
    };

    switch (name) {
      case 'is_standard':
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              minHeight: 40,
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {label}
              </Typography>
              {hint ? (
                <Typography variant="caption" color="text.secondary">
                  {hint}
                </Typography>
              ) : null}
            </Box>
            <Switch
              checked={Boolean(ctx.values.is_standard)}
              onChange={(event) => ctx.onFieldChange('is_standard', event.target.checked)}
              inputProps={{ 'aria-label': label }}
            />
          </Box>
        );
      case 'sessionDays':
        return <FormSelect {...commonSelectProps} options={SESSION_DAY_OPTIONS} />;
      case 'sessionHours':
        return <FormSelect {...commonSelectProps} options={SESSION_HOUR_OPTIONS} />;
      case 'sessionMinutes':
        return <FormSelect {...commonSelectProps} options={SESSION_MINUTE_OPTIONS} />;
      default:
        return null;
    }
  };

  return (
    <FormBuilder
      layout="sections"
      sections={sections}
      values={form}
      errors={errors}
      onFieldChange={updateField}
      renderField={renderProfileField}
    />
  );
}
